import { Badge } from "@/components/ui/badge";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getInstagramOverview, getInstagramMessages, setInstagramMode, updateInstagramSettings, listInstagramKeywords, saveInstagramKeyword, deleteInstagramKeyword, listInstagramBookings } from "@/lib/instagram.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { adminUpdateReservationStatus } from "@/lib/reservations.functions";
import { Instagram } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/instagram")({
  component: InstagramAdmin,
});

const MODES = [
  { v: "ai", label: "AI" },
  { v: "human", label: "Human" },
  { v: "closed", label: "Closed" },
] as const;

function InstagramAdmin() {
  const qc = useQueryClient();
  const overviewFn = useServerFn(getInstagramOverview);
  const msgsFn = useServerFn(getInstagramMessages);
  const modeFn = useServerFn(setInstagramMode);
  const settingsFn = useServerFn(updateInstagramSettings);
  const [selected, setSelected] = useState<string | null>(null);
  const [instructions, setInstructions] = useState("");
  const [aiOn, setAiOn] = useState(true);

  const overview = useQuery({ queryKey: ["ig-overview"], queryFn: () => overviewFn(), refetchInterval: 15000 });
  const msgs = useQuery({
    queryKey: ["ig-msgs", selected],
    queryFn: () => msgsFn({ data: { conversationId: selected! } }),
    enabled: !!selected,
    refetchInterval: 10000,
  });

  useEffect(() => {
    if (overview.data) {
      setInstructions(overview.data.settings.custom_instructions ?? "");
      setAiOn(overview.data.settings.ai_enabled);
    }
  }, [overview.data?.settings.custom_instructions, overview.data?.settings.ai_enabled]);

  const saveSettings = useMutation({
    mutationFn: (clear_pause?: boolean) => settingsFn({ data: { ai_enabled: aiOn, custom_instructions: instructions, clear_pause } }),
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["ig-overview"] }); },
    onError: (e) => toast.error((e as Error).message),
  });
  const changeMode = useMutation({
    mutationFn: (v: { id: string; mode: "ai" | "human" | "closed" }) => modeFn({ data: { conversationId: v.id, mode: v.mode } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ig-overview"] }),
  });

  const d = overview.data;
  const current = d?.conversations.find((c) => c.id === selected);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <Instagram className="h-6 w-6 shrink-0 text-primary" />
        <h1 className="font-display text-xl sm:text-2xl">Instagram</h1>
        {d && (
          <span className={`rounded-full px-3 py-1 text-xs ${d.connected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
            {d.connected ? "Connected" : "Awaiting connection"}
          </span>
        )}
      </div>

      {d?.settings.paused_reason && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm">
          <p>{d.settings.paused_reason}</p>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => saveSettings.mutate(true)}>Resume AI</Button>
        </div>
      )}

      <section className="rounded-lg border border-border p-3 sm:p-4 space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={aiOn} onChange={(e) => setAiOn(e.target.checked)} />
          AI replies automatically to DMs and comments
        </label>
        <Textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Extra instructions for the AI (e.g. this week's promo, tone)…"
          rows={3}
        />
        <Button size="sm" className="w-full sm:w-auto" onClick={() => saveSettings.mutate(false)} disabled={saveSettings.isPending}>Save</Button>
      </section>

      <FeaturesSection />

      <BookingsSection />

      <KeywordsSection />

      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
        <ul className={`rounded-lg border border-border divide-y divide-border max-h-[70vh] overflow-auto ${selected ? "hidden md:block" : ""}`}>
          {d?.conversations.length === 0 && <li className="p-4 text-sm text-muted-foreground">No conversations yet.</li>}
          {d?.conversations.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => setSelected(c.id)}
                className={`w-full p-3 text-left text-sm hover:bg-muted/50 ${selected === c.id ? "bg-muted" : ""}`}
              >
                <div className="flex flex-wrap items-center gap-1.5 font-medium">
                  {c.username ? `@${c.username}` : `User ${c.external_user_id.slice(-6)}`}
                  {c.lead_source && (
                    <Badge variant="secondary" className="text-[10px]" title={`Lead from ${c.lead_source.replace("_", " ")}`}>LEAD</Badge>
                  )}
                  {c.booked && <Badge className="text-[10px]">BOOK</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {c.mode.toUpperCase()} · {new Date(c.last_message_at).toLocaleString("en-IE", { timeZone: "Europe/Dublin" })}
                </div>
              </button>
            </li>
          ))}
        </ul>

        <div className={`rounded-lg border border-border p-3 sm:p-4 min-h-[300px] ${!selected ? "hidden md:block" : ""}`}>
          {!current ? (
            <p className="text-sm text-muted-foreground">Select a conversation.</p>
          ) : (
            <>
              <Button size="sm" variant="ghost" className="mb-2 md:hidden" onClick={() => setSelected(null)}>← All conversations</Button>
              <div className="mb-4 flex flex-wrap gap-2">
                {MODES.map((m) => (
                  <Button key={m.v} size="sm" className="flex-1 sm:flex-none" variant={current.mode === m.v ? "default" : "outline"} onClick={() => changeMode.mutate({ id: current.id, mode: m.v })}>
                    {m.label}
                  </Button>
                ))}
              </div>
              <div className="space-y-2">
                {msgs.data?.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : ""}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.direction === "out" ? "bg-primary/20" : "bg-muted"}`}>
                      {m.source === "comment" && <div className="text-[10px] uppercase text-muted-foreground">comment</div>}
                      <p className="whitespace-pre-wrap">{m.text}</p>
                      {m.error && <p className="mt-1 text-xs text-destructive">Not sent: {m.error}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KeywordsSection() {
  const qc = useQueryClient();
  const listFn = useServerFn(listInstagramKeywords);
  const saveFn = useServerFn(saveInstagramKeyword);
  const delFn = useServerFn(deleteInstagramKeyword);
  const [kw, setKw] = useState("");
  const [reply, setReply] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [editKw, setEditKw] = useState("");
  const [editReply, setEditReply] = useState("");

  const list = useQuery({ queryKey: ["ig-keywords"], queryFn: () => listFn() });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["ig-keywords"] });

  const add = useMutation({
    mutationFn: () => saveFn({ data: { keyword: kw.trim(), reply: reply.trim(), enabled: true } }),
    onSuccess: () => { setKw(""); setReply(""); invalidate(); },
    onError: (e) => toast.error((e as Error).message),
  });
  const saveRow = useMutation({
    mutationFn: (k: { id: string; keyword: string; reply: string; enabled: boolean }) => saveFn({ data: k }),
    onSuccess: () => { setEditing(null); invalidate(); },
    onError: (e) => toast.error((e as Error).message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <section className="rounded-lg border border-border p-4 space-y-3">
      <div>
        <h2 className="font-medium">Quick replies (keywords)</h2>
        <p className="text-sm text-muted-foreground">
          When a customer's DM or comment contains a keyword, we instantly send the saved reply. If no keyword matches, the AI answers.
        </p>
      </div>
      <ul className="space-y-2">
        {(list.data ?? []).map((k) => (
          <li key={k.id} className="rounded border border-border p-3 text-sm space-y-2">
            {editing === k.id ? (
              <div className="space-y-2">
                <Input value={editKw} onChange={(e) => setEditKw(e.target.value)} placeholder="Keyword" />
                <Textarea value={editReply} onChange={(e) => setEditReply(e.target.value)} rows={2} />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveRow.mutate({ id: k.id, keyword: editKw.trim(), reply: editReply.trim(), enabled: k.enabled })} disabled={saveRow.isPending}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                <div className="min-w-0">
                  <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">{k.keyword}</span>
                  <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{k.reply}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={k.enabled}
                      onChange={(e) => saveRow.mutate({ id: k.id, keyword: k.keyword, reply: k.reply, enabled: e.target.checked })}
                    />
                    on
                  </label>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(k.id); setEditKw(k.keyword); setEditReply(k.reply); }}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => remove.mutate(k.id)}>Delete</Button>
                </div>
              </div>
            )}
          </li>
        ))}
        {(list.data ?? []).length === 0 && <li className="text-sm text-muted-foreground">No keywords yet.</li>}
      </ul>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="Keyword (e.g. pool)" className="sm:max-w-[200px]" />
        <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to send" />
        <Button size="sm" onClick={() => add.mutate()} disabled={!kw.trim() || !reply.trim() || add.isPending}>Add</Button>
      </div>
    </section>
  );
}

const STATUS_STYLE: Record<string, string> = {
  confirmed: "bg-primary/20 text-primary",
  cancelled: "bg-destructive/15 text-destructive",
};

function BookingsSection() {
  const qc = useQueryClient();
  const listFn = useServerFn(listInstagramBookings);
  const statusFn = useServerFn(adminUpdateReservationStatus);
  const q = useQuery({ queryKey: ["ig-bookings"], queryFn: () => listFn(), refetchInterval: 15000 });
  const cancel = useMutation({
    mutationFn: (id: string) => statusFn({ data: { id, status: "cancelled", reason: "Cancelled from Instagram admin" } }),
    onSuccess: () => { toast.success("Booking cancelled"); qc.invalidateQueries({ queryKey: ["ig-bookings"] }); },
    onError: (e) => toast.error((e as Error).message),
  });
  const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => new Date(iso).toLocaleString("en-IE", { timeZone: "Europe/Dublin", ...o });
  return (
    <section className="rounded-lg border border-border p-4 space-y-3">
      <h2 className="font-display text-lg">Bookings made via Instagram</h2>
      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {q.data?.length === 0 && <p className="text-sm text-muted-foreground">No upcoming Instagram bookings.</p>}
      {!!q.data?.length && (
        <>
          {/* Mobile: cards */}
          <ul className="space-y-2 sm:hidden">
            {q.data.map((r) => {
              const g = r.guest as { full_name?: string; phone?: string; email?: string | null } | null;
              const active = !["cancelled", "no_show", "completed"].includes(r.status);
              return (
                <li key={r.id} className="rounded-lg border border-border p-3 text-sm space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{fmt(r.starts_at, { weekday: "short", day: "numeric", month: "short" })} · {fmt(r.starts_at, { hour: "2-digit", minute: "2-digit" })}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs capitalize ${STATUS_STYLE[r.status] ?? "bg-muted text-muted-foreground"}`}>{r.status.replaceAll("_", " ")}</span>
                  </div>
                  <div className="text-muted-foreground capitalize">{r.booking_type.replaceAll("_", " ")} · {r.party_size} people · <span className="font-mono text-xs">{r.reference}</span></div>
                  <div>{g?.full_name}<div className="text-xs text-muted-foreground">{g?.phone}{g?.email ? ` · ${g.email}` : ""}</div></div>
                  {active && (
                    <Button size="sm" variant="outline" className="w-full" disabled={cancel.isPending}
                      onClick={() => { if (confirm(`Cancel booking ${r.reference}?`)) cancel.mutate(r.id); }}>
                      Cancel booking
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          {/* Desktop: table */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr><th className="p-2">Date</th><th className="p-2">Time</th><th className="p-2">People</th><th className="p-2">Type</th><th className="p-2">Guest</th><th className="p-2">Ref</th><th className="p-2">Status</th><th className="p-2"></th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {q.data.map((r) => {
                  const g = r.guest as { full_name?: string; phone?: string; email?: string | null } | null;
                  const active = !["cancelled", "no_show", "completed"].includes(r.status);
                  return (
                    <tr key={r.id}>
                      <td className="p-2">{fmt(r.starts_at, { weekday: "short", day: "numeric", month: "short" })}</td>
                      <td className="p-2">{fmt(r.starts_at, { hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="p-2">{r.party_size}</td>
                      <td className="p-2 capitalize">{r.booking_type.replaceAll("_", " ")}</td>
                      <td className="p-2">{g?.full_name}<div className="text-xs text-muted-foreground">{g?.phone}{g?.email ? ` · ${g.email}` : ""}</div></td>
                      <td className="p-2 font-mono text-xs">{r.reference}</td>
                      <td className="p-2"><span className={`rounded-full px-2 py-0.5 text-xs capitalize ${STATUS_STYLE[r.status] ?? "bg-muted text-muted-foreground"}`}>{r.status.replaceAll("_", " ")}</span></td>
                      <td className="p-2 text-right">
                        {active && (
                          <Button size="sm" variant="outline" disabled={cancel.isPending}
                            onClick={() => { if (confirm(`Cancel booking ${r.reference}?`)) cancel.mutate(r.id); }}>
                            Cancel
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function FeaturesSection() {
  const items = [
    ["Answers any question", "Reads each DM or comment and replies with live events, sport, menu, prices and kitchen hours."],
    ["Books tables in the chat", "Pool, darts, food & drinks and sports-viewing tables for 1–10 people. Checks free tables live, then confirms with a reference."],
    ["Follows the booking rules", "2 hours notice, kitchen cut-off, no double-booking, waitlist when full, groups over 10 and Karaoke Terrace go to bookings@plunkets.ie."],
    ["Email confirmation", "Asks for the customer's email and sends the confirmation to them and to bookings@plunkets.ie."],
    ["Instagram updates", "When staff confirm or cancel an Instagram booking, the customer gets a DM automatically."],
    ["Quick replies", "Short messages like \"pool\" or \"menu\" get your saved answer instantly; questions go to the AI."],
    ["Comments → DM", "Comments on posts get a private DM plus a short public reply."],
    ["Human / Closed mode", "Switch any chat to Human to reply yourself, or Closed to stop replies."],
  ];
  return (
    <section className="rounded-lg border border-border p-4">
      <h2 className="font-display text-lg mb-3">What the automation does</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map(([t, d]) => (
          <div key={t} className="rounded-md bg-muted/40 p-3">
            <div className="text-sm font-medium">{t}</div>
            <div className="text-xs text-muted-foreground mt-1">{d}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
